import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PillarType, PillarData, PILLAR_COLORS } from '../../lib/pillarTypes';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { localizeNumber } from '../../utils/rtl';
import { formatCompact } from '../../utils/numberFormat';
import { resolveCommunityAncestors } from '../../Data/ProjectConfig/projectConfigOverrides';
import { communityConfigStore } from '../../Data/store';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './PillarPageHeader.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

/**
 * One descriptive metadata slot in the H2 tray card (DEV-1396). A slot pairs a
 * pre-localized `label` with either a plain `value` string OR a `node` (e.g. the
 * chronicle Subjects chips + editor). Slots are DESCRIPTIVE — dynamic state (a
 * chronicle's reach) belongs in a header status pill, never here.
 */
export interface TraySlot {
  /** Pre-localized label (the caller owns i18n — no hardcoding in this shared component). */
  label: string;
  /** Plain text value. Ignored when `node` is supplied. */
  value?: string;
  /** Rich content for the slot value (chips, editors). Takes precedence over `value`. */
  node?: React.ReactNode;
}

export interface PillarPageHeaderProps {
  pillarType: PillarType;
  data: PillarData;
  /**
   * DEV-1396 — optional override for the project tray's metadata slots. When
   * provided, the tray renders EXACTLY these slots (label + value/node) instead
   * of the hardcoded Category/Status/Timeline/Content project slots. When absent
   * (all project + mini-project consumers), the original three project slots
   * render unchanged. Chronicles pass content-appropriate slots (Type, Subjects).
   */
  traySlots?: TraySlot[];
  /** Override hero image URL (e.g. from config overrides). Takes priority over data.heroImageUrl. */
  heroImageUrl?: string | null;
  /** Optional controls (buttons, menu, etc.) rendered inside the hero area. */
  heroEditControls?: React.ReactNode;
  /** Optional actions (e.g. Follow) rendered in the project hero action zone. */
  headerActions?: React.ReactNode;
  /** Crop overlay rendered at the header level so it fills the full banner area. */
  heroCropOverlay?: React.ReactNode;
  /** When false, the hero image area collapses with animation. Defaults to true. */
  isHeroExpanded?: boolean;
  /** When true, the header overflow is set to visible so dropdown menus aren't clipped. */
  heroMenuOpen?: boolean;
  onBookmark?: () => void;
  onShare?: () => void;
  onEdit?: () => void;
  /** Restore to split-screen (minimize from full-page). */
  onRestore?: () => void;
  onClose?: () => void;
  /** When true, the header suppresses its own heroBleed (parent handles it). */
  suppressBleed?: boolean;
  /** When true, the hero area extends taller so the tab bar can overlap into it. */
  extendForTabs?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Status colour helper                                               */
/* ------------------------------------------------------------------ */

function statusColor(status?: string): string {
  if (!status) return 'var(--text-tertiary)';
  const s = status.toLowerCase();
  if (s.includes('progress') || s.includes('active')) return 'var(--color-success)';
  if (s.includes('plan') || s.includes('draft')) return 'var(--color-warning)';
  if (s.includes('complete') || s.includes('closed')) return 'var(--accent-primary)';
  if (s.includes('risk') || s.includes('delay')) return 'var(--color-error)';
  return 'var(--text-tertiary)';
}

/* ------------------------------------------------------------------ */
/*  Status-KEY colour helper (DEV-1409)                                */
/*                                                                     */
/*  Additive — colors the tray Status value from a stable i18n-free    */
/*  KEY (never the translated display string). Used only by consumers  */
/*  that set PillarData.statusKey (chronicles' reach state today).     */
/*  Returns null for unknown keys so callers fall back to the default  */
/*  tray value color — projects pass no statusKey and are unaffected.  */
/* ------------------------------------------------------------------ */

const STATUS_KEY_COLORS: Record<string, string> = {
  draft: 'var(--text-muted)',
  followers: 'var(--info-text)',
  inReview: 'var(--color-warning)',
  reachingSearch: 'var(--color-success)',
  inCommunityFeed: 'var(--color-success)',
  paused: 'var(--color-error)',
};

function statusKeyColor(statusKey?: string): string | null {
  if (!statusKey) return null;
  return STATUS_KEY_COLORS[statusKey] ?? null;
}

/* ------------------------------------------------------------------ */
/*  Country flag emoji from two-letter code                            */
/* ------------------------------------------------------------------ */

function countryFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  const codePoints = code
    .toUpperCase()
    .split('')
    .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

/* ------------------------------------------------------------------ */
/*  Country name → ISO 3166-1 alpha-2 lookup (for flag resolution)     */
/* ------------------------------------------------------------------ */

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'united states': 'US', 'usa': 'US', 'united states of america': 'US',
  'canada': 'CA', 'france': 'FR', 'germany': 'DE', 'united kingdom': 'GB',
  'australia': 'AU', 'japan': 'JP', 'china': 'CN', 'india': 'IN',
  'brazil': 'BR', 'mexico': 'MX', 'spain': 'ES', 'italy': 'IT',
  'netherlands': 'NL', 'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK',
  'finland': 'FI', 'ukraine': 'UA', 'poland': 'PL', 'russia': 'RU',
  'south korea': 'KR', 'new zealand': 'NZ', 'south africa': 'ZA',
  'argentina': 'AR', 'colombia': 'CO', 'chile': 'CL', 'peru': 'PE',
  'egypt': 'EG', 'nigeria': 'NG', 'kenya': 'KE', 'ghana': 'GH',
  'morocco': 'MA', 'saudi arabia': 'SA', 'israel': 'IL', 'turkey': 'TR',
  'ireland': 'IE', 'portugal': 'PT', 'switzerland': 'CH', 'austria': 'AT',
  'belgium': 'BE', 'greece': 'GR', 'czech republic': 'CZ', 'czechia': 'CZ',
  'romania': 'RO', 'hungary': 'HU', 'singapore': 'SG', 'malaysia': 'MY',
  'indonesia': 'ID', 'thailand': 'TH', 'vietnam': 'VN', 'philippines': 'PH',
  'pakistan': 'PK', 'bangladesh': 'BD', 'sri lanka': 'LK', 'nepal': 'NP',
  'taiwan': 'TW', 'hong kong': 'HK', 'iceland': 'IS', 'luxembourg': 'LU',
  'costa rica': 'CR', 'panama': 'PA', 'uruguay': 'UY', 'ecuador': 'EC',
  'bolivia': 'BO', 'paraguay': 'PY', 'venezuela': 'VE', 'jamaica': 'JM',
  'trinidad and tobago': 'TT', 'dominican republic': 'DO', 'cuba': 'CU',
  'guatemala': 'GT', 'honduras': 'HN', 'el salvador': 'SV', 'nicaragua': 'NI',
  'ethiopia': 'ET', 'tanzania': 'TZ', 'uganda': 'UG', 'rwanda': 'RW',
  'senegal': 'SN', 'cameroon': 'CM', 'algeria': 'DZ', 'tunisia': 'TN',
  'libya': 'LY', 'jordan': 'JO', 'iraq': 'IQ', 'iran': 'IR',
  'united arab emirates': 'AE', 'qatar': 'QA', 'kuwait': 'KW', 'oman': 'OM',
  'bahrain': 'BH', 'lebanon': 'LB', 'syria': 'SY', 'yemen': 'YE',
};

/** Resolve flag emoji from either a 2-letter code or a country name. */
function resolveCountryFlag(codeOrName?: string): string {
  if (!codeOrName) return '';
  // Try direct 2-letter code first
  if (codeOrName.length === 2) return countryFlag(codeOrName);
  // Fall back to name lookup
  const code = COUNTRY_NAME_TO_CODE[codeOrName.toLowerCase().trim()];
  return code ? countryFlag(code) : '';
}

/* ------------------------------------------------------------------ */
/*  Compact timeline string  (e.g. "Jun – Dec 2026")                   */
/* ------------------------------------------------------------------ */

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function compactTimeline(start?: string, end?: string): string {
  if (!start && !end) return '';
  const parse = (d?: string) => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };
  const s = parse(start);
  const e = parse(end);
  if (s && e) {
    const sm = SHORT_MONTHS[s.getMonth()];
    const sy = s.getFullYear();
    const em = SHORT_MONTHS[e.getMonth()];
    const ey = e.getFullYear();
    return sy === ey ? `${sm} \u2013 ${em} ${ey}` : `${sm} ${sy} \u2013 ${em} ${ey}`;
  }
  if (s) return `${SHORT_MONTHS[s.getMonth()]} ${s.getFullYear()} \u2013`;
  if (e) return `\u2013 ${SHORT_MONTHS[e!.getMonth()]} ${e!.getFullYear()}`;
  return '';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PillarPageHeader({
  pillarType,
  data,
  traySlots,
  heroImageUrl,
  heroEditControls,
  headerActions,
  heroCropOverlay,
  isHeroExpanded = true,
  heroMenuOpen = false,
  onBookmark,
  onShare,
  onEdit,
  onRestore,
  onClose,
  suppressBleed = false,
  extendForTabs = false,
}: PillarPageHeaderProps) {
  const { t } = useAppTranslation('pages');
  const pillarColor = PILLAR_COLORS[pillarType];
  const isProject = pillarType === 'projects';

  /* Resolve hero: prop override > data field */
  const effectiveHero = mediaSrc(heroImageUrl || data.heroImageUrl);

  /* Gradient fallback when no hero image */
  const heroBgStyle: React.CSSProperties = effectiveHero
    ? { backgroundImage: `url(${effectiveHero})` }
    : {
        background: `linear-gradient(135deg, ${pillarColor}dd 0%, ${pillarColor}88 40%, var(--bg-secondary) 100%)`,
      };

  /* Subtle gradient overlay — bottom edge only, matches community header */
  const overlayStyle: React.CSSProperties = {
    background: effectiveHero
      ? 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.12) 35%, transparent 65%)'
      : 'linear-gradient(to top, rgba(0,0,0,0.30) 0%, transparent 50%)',
  };

  /* Location string (non-project pillars) */
  const locationParts = [data.communityL3, data.communityL2, data.communityL1].filter(Boolean);
  const locationStr = locationParts.join(', ');

  /* Date range (non-project pillars) */
  const dateRange =
    data.startDate || data.endDate
      ? [data.startDate, data.endDate].filter(Boolean).join(' \u2192 ')
      : '';

  /* Flag — try explicit code first, then resolve from country name (communityL1) */
  const flagEmoji = resolveCountryFlag(data.countryFlagCode) || resolveCountryFlag(data.communityL1);

  /* Project-specific: compact timeline for tray card */
  const timeline = compactTimeline(data.startDate, data.endDate);

  const navigate = useNavigate();

  /* Community breadcrumb path for projects tray: lowest → highest, with active status */
  const communityPath = useMemo(() => {
    const locGuid = data.primaryLocationGuid?.trim() || '';
    if (locGuid) {
      const ancestors = resolveCommunityAncestors(locGuid);
      const leafName = data.communityL3 || data.communityL2 || data.communityL1 || '';
      const leafGuid = locGuid.toUpperCase();
      const leafCfg = communityConfigStore.get(leafGuid);
      const segments: { guid: string; name: string; isActive: boolean }[] = [];
      if (leafName) segments.push({ guid: leafGuid, name: leafName, isActive: leafCfg?.is_active === true });
      for (let i = ancestors.length - 1; i >= 0; i--) {
        const a = ancestors[i];
        const cfg = communityConfigStore.get(a.guid.toUpperCase());
        segments.push({ guid: a.guid, name: a.name, isActive: cfg?.is_active === true });
      }
      return segments;
    }
    // Fallback — no GUID, plain names, no links
    return [data.communityL3, data.communityL2, data.communityL1]
      .filter(Boolean)
      .map((n) => ({ guid: '', name: n as string, isActive: false }));
  }, [data]);

  return (
    <div
      className={`${styles.headerOuter} ${!isHeroExpanded ? styles.headerOuterCollapsed : ''} ${suppressBleed ? styles.headerOuterNoRadius : ''} ${extendForTabs ? styles.headerOuterExtended : ''}`}
      style={heroMenuOpen ? { overflow: 'visible' } : undefined}
    >
      {/* Blurred bleed background — suppressed when parent Shell provides a shared bleed */}
      {!suppressBleed && effectiveHero && isHeroExpanded && (
        <div className={styles.heroBleed} style={{ backgroundImage: `url(${effectiveHero})` }} />
      )}
    <div className={`${styles.header} ${!isHeroExpanded ? styles.headerCollapsed : ''}`} style={heroMenuOpen ? { overflow: 'visible', zIndex: 20 } : undefined}>
      {/* Background */}
      <div className={styles.heroBg} style={heroBgStyle} />
      <div className={styles.heroOverlay} style={overlayStyle} />

      {/* Crop overlay — rendered at header level so it fills the full banner area */}
      {isHeroExpanded && heroCropOverlay}

      {/* Hero edit controls — non-project: render normally; project: rendered in top-right zone */}
      {!isProject && isHeroExpanded && heroEditControls}

      {/* ── Project: top-right control zone (actions + cover photo) ── */}
      {isProject && isHeroExpanded && (
        <div className={styles.projectTopRight}>
          <div className={styles.actions}>
            {headerActions}
            {onEdit && (
              <button className={styles.actionBtn} onClick={onEdit} title="Project settings" aria-label="Project settings">
                <IconSettings />
              </button>
            )}
            {onRestore && (
              <button className={styles.actionBtn} onClick={onRestore} title="Restore to split view" aria-label="Restore to split-screen view">
                <IconRestore />
              </button>
            )}
            {onClose && (
              <button className={styles.actionBtn} onClick={onClose} title="Close" aria-label="Close">
                <IconClose />
              </button>
            )}
          </div>
          {/* Wrapper resets heroEditControls' own absolute positioning so it flows in the flex column */}
          <div className={styles.heroEditControlsInline}>
            {heroEditControls}
          </div>
        </div>
      )}

      {/* ── H2 Card Tray (projects only) ── */}
      {isProject && isHeroExpanded && (
        <div className={styles.tray}>
          {/* Avatar + project name + community path */}
          <div className={styles.trayTop}>
            {/* DEV-991 — Clickable owner avatar.
                User owner → /profile/{guid} with initialTab='about' (DEV-1455:
                the profile Impact tab moved to the My Impact page).
                Community owner → /communities/{guid}#alignment. */}
            {(() => {
              const ownerNavigable = !!(data.ownerKind && data.ownerGuid && data.ownerKind !== ('unknown' as any));
              const onActivate = () => {
                if (!ownerNavigable || !data.ownerGuid) return;
                if (data.ownerKind === 'user') {
                  navigate(`/profile/${data.ownerGuid}`, { state: { initialTab: 'about' } });
                } else {
                  navigate(`/communities/${data.ownerGuid}#alignment`);
                }
              };
              return (
                <div
                  className={styles.trayAvatar}
                  role={ownerNavigable ? 'link' : undefined}
                  tabIndex={ownerNavigable ? 0 : undefined}
                  title={ownerNavigable ? `Open ${data.ownerDisplayName || 'owner'}` : undefined}
                  style={{ cursor: ownerNavigable ? 'pointer' : 'default' }}
                  onClick={onActivate}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onActivate();
                    }
                  }}
                >
                  {data.ownerAvatarUrl ? (
                    <img src={mediaSrc(data.ownerAvatarUrl) ?? undefined} alt="" />
                  ) : data.logoUrl ? (
                    <img src={mediaSrc(data.logoUrl) ?? undefined} alt="" />
                  ) : (
                    data.name.charAt(0).toUpperCase()
                  )}
                </div>
              );
            })()}
            <div className={styles.trayTitleGroup}>
              <h1 className={styles.trayName}>{data.name}</h1>
              {/* Community breadcrumb path: flag + lowest → highest */}
              {communityPath.length > 0 && (
                <div className={styles.trayCommunityPath}>
                  {flagEmoji && <span className={styles.trayCommunityFlag}>{flagEmoji}</span>}
                  {communityPath.map((seg, i) => (
                    <React.Fragment key={seg.guid || seg.name}>
                      {i > 0 && <span className={styles.trayCommunityArrow}>{'\u203A'}</span>}
                      {seg.isActive && seg.guid ? (
                        <span
                          role="link"
                          tabIndex={0}
                          className={styles.trayCommunityLinkActive}
                          onClick={() => navigate(`/communities/${seg.guid}`)}
                          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/communities/${seg.guid}`); }}
                        >
                          {seg.name}
                        </span>
                      ) : (
                        <span className={styles.trayCommunityLink}>{seg.name}</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.trayDivider} />

          {/* Frosted metadata card.
              DEV-1396 \u2014 when the caller supplies `traySlots`, render those
              descriptive slots verbatim. Otherwise (projects + mini-projects)
              render the original hardcoded Category/Status/Timeline/Content
              slots unchanged. */}
          <div className={styles.trayCard}>
            {traySlots ? (
              traySlots.map((slot, i) => (
                <div key={`${slot.label}-${i}`}>
                  <div className={styles.trayCardLabel}>{slot.label}</div>
                  <div className={styles.trayCardValue}>
                    {slot.node ?? (slot.value || '\u2014')}
                  </div>
                </div>
              ))
            ) : (
              <>
                <div>
                  <div className={styles.trayCardLabel}>{t('projectDetail.tray.category')}</div>
                  <div className={styles.trayCardValue}>{data.category || '\u2014'}</div>
                </div>
                <div>
                  <div className={styles.trayCardLabel}>{t('projectDetail.tray.status')}</div>
                  {/* DEV-1409 \u2014 when a consumer supplies a stable statusKey, tint
                      the value from that key, not the translated string. Projects
                      pass no statusKey \u2192 default value color. */}
                  <div
                    className={styles.trayCardValue}
                    style={statusKeyColor(data.statusKey) ? { color: statusKeyColor(data.statusKey)! } : undefined}
                  >
                    {data.status || '\u2014'}
                  </div>
                </div>
                <div>
                  <div className={styles.trayCardLabel}>{t('projectDetail.tray.timeline')}</div>
                  <div className={styles.trayCardValue}>{timeline || '\u2014'}</div>
                </div>
                <div>
                  <div className={styles.trayCardLabel}>{t('projectDetail.tray.content')}</div>
                  <div className={styles.trayCardValue}>{data.contentStatus || '\u2014'}</div>
                </div>
                {/* DEV-1112 \u2014 Lifecycle chip. Mini-Project sets this from
                    mp.status; Full Project leaves it undefined and we skip
                    the chip entirely. Drives KPI dot short-circuits in
                    computeKpiStatus.ts so it warrants visibility. */}
                {data.lifecycleStatus && (
                  <div>
                    <div className={styles.trayCardLabel}>{t('projectDetail.tray.lifecycle')}</div>
                    <div className={styles.trayCardValue}>
                      {t(`projectDetail.tray.lifecycleStates.${data.lifecycleStatus}`, {
                        defaultValue: data.lifecycleStatus,
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Project: bottom-right social stats ── */}
      {isProject && isHeroExpanded && (
        <div className={styles.projectBottomRight}>
          <span className={styles.socialStat}>
            <IconUsers />
            <span className={styles.socialStatValue}>{formatCompact(data.followersCount ?? 0)}</span>
            {t('projectDetail.followers')}
          </span>
          <span className={styles.socialStat}>
            <IconHand />
            <span className={styles.socialStatValue}>{formatCompact(data.contributorsCount ?? 0)}</span>
            {t('projectDetail.contributors')}
          </span>
        </div>
      )}

      {/* Content */}
      <div className={`${styles.headerContent} ${isProject && isHeroExpanded ? styles.headerContentWithTray : ''}`}>
        {/* Top row: logo + title + actions (non-project pillars only show logo+title) */}
        {!isProject && (
          <div className={styles.topRow}>
            <div className={styles.logo}>
              {data.logoUrl ? (
                <img src={mediaSrc(data.logoUrl) ?? undefined} alt="" />
              ) : (
                data.name.charAt(0).toUpperCase()
              )}
            </div>

            <div className={styles.titleBlock}>
              <h1 className={styles.name}>{data.name}</h1>
              <div className={styles.meta}>
                {data.category && <span>{data.category}</span>}
                {data.category && data.status && <span className={styles.metaDot} />}
                {data.status && (
                  <span className={styles.statusBadge}>
                    <span
                      className={styles.statusDot}
                      style={{ background: statusColor(data.status) }}
                    />
                    {data.status}
                  </span>
                )}
                {data.contentStatus && (
                  <>
                    <span className={styles.metaDot} />
                    <span>{data.contentStatus}</span>
                  </>
                )}
              </div>
            </div>

            <div className={styles.actions}>
              {onBookmark && (
                <button className={styles.actionBtn} onClick={onBookmark} title="Bookmark" aria-label="Bookmark">
                  <IconBookmark />
                </button>
              )}
              {onShare && (
                <button className={styles.actionBtn} onClick={onShare} title="Share" aria-label="Share">
                  <IconShare />
                </button>
              )}
              {onEdit && (
                <button className={styles.actionBtn} onClick={onEdit} title="Project settings" aria-label="Project settings">
                  <IconSettings />
                </button>
              )}
              {onRestore && (
                <button className={styles.actionBtn} onClick={onRestore} title="Restore to split view" aria-label="Restore to split-screen view">
                  <IconRestore />
                </button>
              )}
              {onClose && (
                <button className={styles.actionBtn} onClick={onClose} title="Close" aria-label="Close">
                  <IconClose />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Info row: flag, location, dates (non-project pillars only) */}
        {!isProject && (locationStr || dateRange || flagEmoji) && (
          <div className={styles.infoRow}>
            {flagEmoji && <span className={styles.flag}>{flagEmoji}</span>}
            {locationStr && <span>{locationStr}</span>}
            {locationStr && dateRange && <span className={styles.metaDot} />}
            {dateRange && <span>{dateRange}</span>}
          </div>
        )}

        {/* SDG badges */}
        {data.sdgBadges && data.sdgBadges.length > 0 && (
          <div className={styles.sdgRow}>
            {data.sdgBadges.slice(0, 6).map((badge) => (
              <span key={badge} className={styles.sdgBadge}>
                {localizeNumber(badge)}
              </span>
            ))}
            {data.sdgBadges.length > 6 && (
              <span className={styles.sdgBadge}>+{localizeNumber(data.sdgBadges.length - 6)}</span>
            )}
          </div>
        )}

        {/* Social stats — non-project: full-width bar; project: positioned bottom-right */}
        {!isProject && (
          <div className={styles.socialBar}>
            <span className={styles.socialStat}>
              <IconUsers />
              <span className={styles.socialStatValue}>{formatCompact(data.followersCount ?? 0)}</span>
              {t('projectDetail.followers')}
            </span>
            <span className={styles.socialStat}>
              <IconHand />
              <span className={styles.socialStatValue}>{formatCompact(data.contributorsCount ?? 0)}</span>
              {t('projectDetail.contributors')}
            </span>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline SVG icons                                                   */
/* ------------------------------------------------------------------ */

function IconBookmark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconShare() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

/*
 * IconSettings — gear icon for the project-settings button on the project
 * header. Was IconEdit (pencil-and-paper). Skip flagged that pencil reads
 * as "edit content" and gear is the right semantic for "configure how this
 * thing behaves" — Project Settings is configuration, not content editing.
 *
 * Pencil affordance is preserved elsewhere in the app for genuine content
 * editing (e.g. KPI thermometer pencil → opens KPI configuration wizard
 * for that specific KPI's settings — yes, also "settings" in a narrower
 * sense, but tied to a single editable entity rather than a panel of
 * project-wide configuration). The distinction holds: gear for the
 * project-level settings panel, pencil for per-entity edits.
 */
function IconSettings() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconRestore() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconHand() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8V6a2 2 0 1 0-4 0v2" />
      <path d="M14 8V4a2 2 0 1 0-4 0v4" />
      <path d="M10 8V6a2 2 0 1 0-4 0v6" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8H8a8 8 0 0 1-6-2.69" />
    </svg>
  );
}
